import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Bar, BarChart, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { DollarSign, ShoppingBag, Users, Palette, RefreshCw, Sparkles, Upload, FileText, Mail, Trash2, Archive, ArchiveRestore, LayoutDashboard, Settings as SettingsIcon, Bell, TrendingUp, UserCog, Search, Shield, Receipt, FileDown, Edit3, Plus, Package, Send, Reply, X, Flag, ShieldCheck, ShieldAlert, ShieldX, Crown, Award, Gift, Minus, Power, PowerOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import RichEditor from '@/components/RichEditor';
import { useCurrency } from '@/context/CurrencyContext';
import { useConfirm } from '@/context/ConfirmContext';
import Pagination from '@/components/Pagination';
import BulkActionBar from '@/components/BulkActionBar';
import { useSelection } from '@/lib/useSelection';
import { openInvoice } from '@/lib/invoice';
import { Kpi } from './_shared';
import SEO from '@/components/SEO';
import DataCleanupCard from './DataCleanupCard';
import SettingsField from './SettingsField';
import { useSiteSettings } from '@/context/SiteSettingsContext';

export const SettingsSection = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const { refresh: refreshPublicSettings } = useSiteSettings();
  const load = () => api.get('/admin/settings').then(({ data }) => setForm(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.patch('/admin/settings', form);
      toast.success('Settings saved');
      load();
      refreshPublicSettings();
    }
    catch { toast.error('Save failed'); } finally { setLoading(false); }
  };

  if (!form) return <div style={{ color: 'var(--jw-text-muted)' }}>Loading...</div>;

  return (
    <div className="space-y-4">
      <Card className="jw-card p-5 space-y-4">
        <div className="flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-orange-400" /><h3 className="font-semibold">Site & SEO</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="site_name" label="Site name" form={form} setForm={setForm} />
          <SettingsField k="site_url" label="Canonical site URL" help="Used for canonical tags & sitemap" form={form} setForm={setForm} />
        </div>
        <SettingsField k="site_description" label="SEO description" textarea help="Default meta description for search engines" form={form} setForm={setForm} />
        <SettingsField k="site_keywords" label="SEO keywords" textarea help="Comma-separated keywords" form={form} setForm={setForm} />
        <SettingsField k="og_image_url" label="Open Graph image URL" help="Displayed on social shares (1200x630)" form={form} setForm={setForm} />
        <div className="pt-2 border-t" style={{ borderColor: 'var(--jw-border)' }}>
          <div className="flex flex-wrap gap-2 text-xs">
            <a href="/api/sitemap.xml" target="_blank" rel="noreferrer" data-testid="admin-view-sitemap-link" className="underline" style={{ color: 'var(--jw-text-muted)' }}>View live sitemap.xml →</a>
            <span style={{ color: 'var(--jw-text-subtle)' }}>·</span>
            <a href="/api/robots.txt" target="_blank" rel="noreferrer" data-testid="admin-view-robots-link" className="underline" style={{ color: 'var(--jw-text-muted)' }}>robots.txt →</a>
          </div>
        </div>
      </Card>

      <Card className="jw-card p-5 space-y-4" data-testid="admin-store-closure-card">
        <div className="flex items-center gap-2">
          {form.store_open === false
            ? <PowerOff className="w-4 h-4 text-rose-400" />
            : <Power className="w-4 h-4 text-emerald-400" />}
          <h3 className="font-semibold">Order intake</h3>
          <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full ${form.store_open === false ? 'bg-rose-500/15 text-rose-300' : 'bg-emerald-500/15 text-emerald-300'}`} data-testid="admin-store-status-pill">
            {form.store_open === false ? 'Closed' : 'Accepting orders'}
          </span>
        </div>
        <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>Toggle off to temporarily stop new orders and checkout site-wide. A dismissable banner will show the message below on every customer page in the visitor&apos;s language.</p>
        <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-elev)' }}>
          <input
            type="checkbox"
            checked={form.store_open !== false}
            onChange={(e) => setForm({ ...form, store_open: e.target.checked })}
            data-testid="admin-store-open-toggle"
            className="mt-0.5 w-4 h-4"
          />
          <div>
            <p className="font-medium text-sm">Accept new orders</p>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--jw-text-subtle)' }}>When off, Add-to-cart, cart checkout and place-order are disabled everywhere. Users can still browse the site.</p>
          </div>
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="store_closed_message" label="Closure message (English)" textarea help="Shown to visitors when orders are paused" form={form} setForm={setForm} />
          <SettingsField k="store_closed_message_ar" label="Closure message (Arabic)" textarea help="رسالة تُعرض عند إيقاف الطلبات مؤقتًا" form={form} setForm={setForm} />
        </div>
      </Card>

      <Card className="jw-card p-5 space-y-4">
        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-orange-400" /><h3 className="font-semibold">Contact & Business</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="contact_email" label="Contact email" form={form} setForm={setForm} />
          <SettingsField k="contact_phone" label="Contact phone" form={form} setForm={setForm} />
        </div>
        <SettingsField k="business_address" label="Business address" form={form} setForm={setForm} />
        <SettingsField k="footer_tagline" label="Footer tagline" textarea form={form} setForm={setForm} />
      </Card>

      <Card className="jw-card p-5 space-y-4">
        <div className="flex items-center gap-2"><Receipt className="w-4 h-4 text-orange-400" /><h3 className="font-semibold">Invoice template</h3></div>
        <p className="text-[11px]" style={{ color: 'var(--jw-text-subtle)' }}>These fields appear on every invoice PDF. Invoices always render in SAR and include two QR codes (view + pay).</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="invoice_logo_url" label="Logo image URL" help="Shown on the top-left of the invoice (leave blank for gradient monogram)" form={form} setForm={setForm} />
          <SettingsField k="invoice_payment_url" label="Payment link (Pay QR)" help="Base URL. We append ?order=…&amount=… for the payment QR" form={form} setForm={setForm} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="invoice_vat_number" label="VAT number" form={form} setForm={setForm} />
          <SettingsField k="invoice_cr_number" label="Commercial Registration (CR) #" form={form} setForm={setForm} />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <SettingsField k="invoice_bank_name" label="Bank name" form={form} setForm={setForm} />
          <SettingsField k="invoice_bank_account_name" label="Account holder" form={form} setForm={setForm} />
          <SettingsField k="invoice_bank_iban" label="IBAN" help="Displayed under the QR codes" form={form} setForm={setForm} />
        </div>
        <SettingsField k="invoice_thank_you" label="Thank you message" form={form} setForm={setForm} />
        <SettingsField k="invoice_footer_note" label="Payment note" textarea form={form} setForm={setForm} />
        <SettingsField k="invoice_terms" label="Terms line" textarea help="Legal / VAT / currency disclosure" form={form} setForm={setForm} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={async () => {
            const { data } = await api.get('/admin/orders?archived=false');
            const first = ((data && data.items) || [])[0];
            if (!first) { toast.error('Place at least one order to preview the invoice.'); return; }
            openInvoice(first.id);
          }} data-testid="admin-invoice-preview-button" className="jw-btn-gloss-ghost">
            <FileDown className="w-4 h-4 mr-1" /> Preview latest invoice
          </Button>
        </div>
      </Card>

      <Card className="jw-card p-5 space-y-4">
        <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-orange-400" /><h3 className="font-semibold">Social</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SettingsField k="social_instagram" label="Instagram URL" form={form} setForm={setForm} />
          <SettingsField k="social_twitter" label="X (Twitter) URL" form={form} setForm={setForm} />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={loading} data-testid="admin-settings-save-button" className="jw-btn-primary">{loading ? 'Saving...' : 'Save settings'}</Button>
      </div>

      <DataCleanupCard />
    </div>
  );
};

export default SettingsSection;
